/**
 * DebugPanel
 *
 * Presentational component: renders whatever debug options are passed in.
 * It does not know what each option means; it just displays id/label
 * (and optional description), and calls onChange when the user toggles.
 * Toasts = user-facing messages we show as popups. Logs = SignalR/debug
 * entries (panel only, never popups).
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@/ui';

// ============= Types =============

export interface DebugOptionItem {
  id: string;
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  /** Optional short description shown under the label (e.g. "User-facing popups" vs "Panel only"). */
  description?: string;
}

export interface DebugPanelProps {
  /** List of options to show (id, label, value, onChange, optional description). */
  options: DebugOptionItem[];
  /** Label for the trigger button. */
  triggerLabel?: string;
}

// ============= Styled =============

const Trigger = styled(Button)`
  margin-left: 4px;
`;

const Panel = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 260px;
  padding: 12px;
  background: #1e293b;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
`;

const PanelNote = styled.p`
  margin: 0 0 10px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #334155;
  font-size: 0.7rem;
  color: #94a3b8;
  line-height: 1.3;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #e2e8f0;

  input {
    cursor: pointer;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const OptionLabelBlock = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionDescription = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
`;

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

// ============= Component =============

export const DebugPanel: React.FC<DebugPanelProps> = ({
  options,
  triggerLabel = 'Debug',
}) => {
  const [open, setOpen] = useState(false);

  if (options.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Trigger
        variant="block"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {triggerLabel}
      </Trigger>
      {open && (
        <>
          <div
            role="presentation"
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
            onClick={() => setOpen(false)}
          />
          <Panel role="dialog" aria-label="Debug options">
            <PanelNote>
              Toasts = user-facing popups. Logs = SignalR/debug (panel only).
            </PanelNote>
            {options.map((opt) => (
              <OptionRow key={opt.id}>
                <input
                  type="checkbox"
                  checked={opt.value}
                  onChange={(e) => opt.onChange(e.target.checked)}
                  aria-label={opt.description ? `${opt.label}: ${opt.description}` : opt.label}
                />
                <OptionLabelBlock>
                  <span>{opt.label}</span>
                  {opt.description && (
                    <OptionDescription>{opt.description}</OptionDescription>
                  )}
                </OptionLabelBlock>
              </OptionRow>
            ))}
          </Panel>
        </>
      )}
    </Wrapper>
  );
};

DebugPanel.displayName = 'DebugPanel';
